import { Info, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  useNewVersionAvailable,
  type NewVersionReason,
} from "~/hooks/app-version/useNewVersionAvailable";

const COPY: Record<NewVersionReason, { title: string; body: string }> = {
  "new-version": {
    title: "A new version of Palate is available",
    body: "Reload to get it. If you are in the middle of something, finish and save first.",
  },
  "load-failure": {
    title: "Part of Palate failed to load",
    body: "This usually means a new version was released while this page was open. Reload to fix it — unsaved changes on this page will be lost.",
  },
};

/**
 * Bug-255 — "User should at least get a prompt to reload/refresh if that is
 * needed after a long idle time."
 *
 * Tells a tab that has outlived its build — a newer one is live, or part of
 * this one could not be fetched — and offers a one-click Reload. DESIGN_SYSTEM.md
 * § Banner `info` variant, floated at the bottom like ServiceUnavailableBanner
 * floats at the top. Never blocks anything and never reloads by itself: the page
 * may hold a half-filled form, and the user decides when.
 *
 * Mounted in root.tsx's Layout, not App, so it is still on screen when a route
 * crashes into an ErrorBoundary — which is exactly when a failed chunk matters.
 * Body text is the blue-700 step (AA on blue-50), as AnnouncementBanner does.
 */
export function NewVersionPrompt() {
  const { reason, dismiss } = useNewVersionAvailable();

  if (!reason) return null;

  const { title, body } = COPY[reason];

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[9998] flex justify-center px-4">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="new-version-prompt"
        className="pointer-events-auto flex w-full max-w-lg items-start gap-3 rounded-[12px] border border-solid border-blue-200 bg-blue-50 p-4 shadow-md"
      >
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 p-1.5">
          <Info className="size-4 text-blue-600" aria-hidden="true" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-bold leading-5 text-blue-800">{title}</p>
            <p className="text-xs leading-4 text-blue-700">{body}</p>
          </div>
          <div>
            <Button
              type="button"
              size="sm"
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="-m-1 flex size-6 shrink-0 items-center justify-center rounded text-blue-700 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
